import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import LanguageSwitcher from '../components/LanguageSwitcher';
import BrandLogo from '../components/BrandLogo';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const { t } = useLanguage();
  // Where to send the user after login — supports ?next=/path so flows like
  // the Instant Pricing "Login to Preview" CTA can resume on their original
  // page once authenticated.
  const nextPath = (() => {
    try {
      const u = new URL(window.location.href);
      const n = u.searchParams.get('next');
      // Only allow same-origin relative paths.
      return n && n.startsWith('/') ? n : '/dashboard';
    } catch { return '/dashboard'; }
  })();
  const [loginType, setLoginType] = useState('phone');
  const [loading, setLoading] = useState(false);
  
  const [phoneData, setPhoneData] = useState({ phone: '' });
  const [emailData, setEmailData] = useState({ email: '', password: '' });
  // OTP gate for phone login (Play Store account-takeover rule)
  const [otpStep, setOtpStep] = useState('idle'); // 'idle' | 'sent' | 'verifying'
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  React.useEffect(() => {
    if (user) {
      navigate(nextPath);
    }
  }, [user, navigate, nextPath]);

  React.useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => setResendIn(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const sendLoginOtp = async () => {
    if (!phoneData.phone || phoneData.phone.length < 8) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setSendingOtp(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/otp/send`, {
        phone: phoneData.phone,
        purpose: 'login',
      });
      setOtpStep('sent');
      setResendIn(30);
      if (data?.otp_for_testing) {
        toast.success(`OTP sent (dev mode): ${data.otp_for_testing}`, { duration: 8000 });
      } else {
        toast.success('OTP sent to your phone via SMS');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handlePhoneLogin = async (e, codeOverride) => {
    if (e && e.preventDefault) e.preventDefault();
    const code = (codeOverride ?? otpCode).replace(/\D/g, '');
    if (otpStep !== 'sent' || code.length < 4) {
      toast.error('Please enter the OTP sent to your phone');
      return;
    }
    setLoading(true);
    try {
      // 1) Verify the OTP first (server marks phone as verified for 30 min)
      await axios.post(`${BACKEND_URL}/api/otp/verify`, {
        phone: phoneData.phone,
        otp: code,
      });
      // 2) Now exchange the verification for a long-lived session
      await axios.post(
        `${BACKEND_URL}/api/auth/login-phone`,
        { phone: phoneData.phone },
        { withCredentials: true },
      );
      toast.success('Logged in — you\'ll stay signed in on this device');
      await checkAuth();
      navigate(nextPath);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Login failed';
      toast.error(msg);
      if (typeof msg === 'string' && msg.toLowerCase().includes('invalid otp')) {
        // Let user retry the OTP
        setOtpCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email: emailData.email, password: emailData.password },
        { withCredentials: true }
      );
      toast.success('Login successful!');
      await checkAuth();
      navigate(nextPath);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand-50 flex items-start sm:items-center justify-center p-4 sm:p-8 pb-44 sm:pb-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex justify-center"><BrandLogo size="lg" linkTo="/" /></div>
          <div className="mt-3 flex justify-center"><LanguageSwitcher /></div>
        </div>

        <Card className="bg-white border-[#E8E1D5]">
          <CardHeader>
            <CardTitle className="font-heading text-3xl">{t('auth.welcome_back')}</CardTitle>
            <CardDescription>{t('auth.login_to_account')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={loginType} onValueChange={setLoginType} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="phone" data-testid="phone-login-tab">{t('auth.phone')}</TabsTrigger>
                <TabsTrigger value="email" data-testid="email-login-tab">{t('auth.email_admin')}</TabsTrigger>
              </TabsList>

              {/* Phone Login — OTP-gated */}
              <TabsContent value="phone">
                <form onSubmit={handlePhoneLogin} className="space-y-5">
                  <div>
                    <Label htmlFor="phone" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                      {t('auth.phone_number')}
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+919876543210"
                      value={phoneData.phone}
                      onChange={(e) => {
                        setPhoneData({ phone: e.target.value });
                        if (otpStep !== 'idle') { setOtpStep('idle'); setOtpCode(''); }
                      }}
                      required
                      disabled={otpStep === 'sent'}
                      className="rounded-none border-[#E8E1D5] focus:ring-brand-primary disabled:bg-sand-100"
                      data-testid="login-phone-input"
                    />
                  </div>

                  {otpStep === 'idle' && (
                    <Button
                      type="button"
                      onClick={sendLoginOtp}
                      disabled={sendingOtp || !phoneData.phone}
                      className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none disabled:opacity-50"
                      data-testid="send-login-otp-btn"
                    >
                      {sendingOtp ? 'Sending OTP...' : 'Send OTP to Phone'}
                    </Button>
                  )}

                  {otpStep === 'sent' && (
                    <>
                      <div>
                        <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                          Enter 6-digit OTP
                        </Label>
                        <Input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={otpCode}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setOtpCode(v);
                            // Auto-submit login the instant the 6th digit
                            // lands. Pass `v` directly because the otpCode
                            // state hasn't committed yet at this point.
                            if (v.length === 6 && !loading) {
                              handlePhoneLogin({ preventDefault: () => {} }, v);
                            }
                          }}
                          placeholder="123456"
                          className="rounded-none border-[#E8E1D5] focus:ring-brand-primary font-mono tracking-widest text-lg"
                          data-testid="login-otp-input"
                          autoFocus
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs text-text-secondary">
                        <span>OTP sent to {phoneData.phone}</span>
                        {resendIn > 0 ? (
                          <span data-testid="login-otp-resend-cooldown">Resend in {resendIn}s</span>
                        ) : (
                          <button
                            type="button"
                            onClick={sendLoginOtp}
                            disabled={sendingOtp}
                            className="text-brand-primary hover:underline font-bold"
                            data-testid="login-otp-resend-btn"
                          >
                            Resend OTP
                          </button>
                        )}
                      </div>
                      <Button
                        type="submit"
                        disabled={loading || otpCode.length < 4}
                        className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none disabled:opacity-50"
                        data-testid="phone-login-btn"
                      >
                        {loading ? 'Logging in...' : 'Verify & Login'}
                      </Button>
                      <p className="text-[10px] text-text-secondary text-center italic">
                        After login you'll stay signed in on this device.
                      </p>
                    </>
                  )}
                </form>
              </TabsContent>

              {/* Email Login (Admin) */}
              <TabsContent value="email">
                <form onSubmit={handleEmailLogin} className="space-y-6">
                  <div>
                    <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={emailData.email}
                      onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                      required
                      className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                      Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={emailData.password}
                      onChange={(e) => setEmailData({ ...emailData, password: e.target.value })}
                      required
                      className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
                    data-testid="email-login-btn"
                  >
                    {loading ? '...' : t('common.login')}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="mt-6 text-center text-sm text-text-secondary">
              {t('auth.no_account')}{' '}
              <Link to="/register" className="text-brand-primary hover:underline" data-testid="register-link">
                {t('auth.register_here')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
